import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Eye, EyeOff, ArrowLeft } from "lucide-react";
import { signUpSchema, signInSchema, resetPasswordSchema } from "@/lib/validations/auth";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ValuePropositionCarousel } from "@/components/ValuePropositionCarousel";

// GDPR Article 7: Conditions for consent - demonstrable, specific, informed, unambiguous
// DPA 2018 Schedule 1, Part 2: Criminal conviction data processing requires substantial public interest

export default function Auth() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [isSignUp, setIsSignUp] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    full_name: "",
    job_title: "",
    company_name: "",
    role: "" as "candidate" | "employer_service_provider" | "client" | "",
  });

  // Detect if user came from employer section
  const isEmployerContext = searchParams.get('context') === 'employer';

  // GDPR Article 7: Demonstrable consent records
  const [consents, setConsents] = useState({
    terms: false,        // GDPR Article 6(1)(b): Contract
    privacy: false,      // GDPR Article 13: Transparency
    criminal_record: false, // DPA 2018 Schedule 1, Part 2: Special category
    data_sharing: false, // GDPR Article 6(1)(a): Consent for non-essential processing
  });

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setValidationErrors({});

    try {
      // Validate input with zod schema
      const validationResult = signUpSchema.safeParse(formData);
      
      if (!validationResult.success) {
        const errors: Record<string, string> = {};
        validationResult.error.issues.forEach((err) => {
          if (err.path[0]) {
            errors[err.path[0].toString()] = err.message;
          }
        });
        setValidationErrors(errors);
        setIsLoading(false);
        return;
      }

      // GDPR Article 7(1): Consent must be freely given, specific, informed
      if (!consents.terms || !consents.privacy) {
        toast({
          title: "Consent Required",
          description: "You must accept the Terms of Service and Privacy Policy to create an account.",
          variant: "destructive",
        });
        setIsLoading(false);
        return;
      }

      // DPA 2018 Schedule 1, Part 2: Criminal conviction consent for candidates
      if (formData.role === "candidate" && !consents.criminal_record) {
        toast({
          title: "Consent Required",
          description: "Candidates must consent to criminal record processing under UK data protection law.",
          variant: "destructive",
        });
        setIsLoading(false);
        return;
      }

      // GDPR Article 6: User authentication via contract basis
      const { data, error } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          data: {
            full_name: formData.full_name,
          },
          emailRedirectTo: `${window.location.origin}/`,
        },
      });

      if (error) throw error;

      if (data.user) {
        // Record user role (Purpose Limitation: GDPR Article 5(1)(b))
        if (formData.role) {
          const { error: roleError } = await supabase
            .from("user_roles")
            .insert([{ 
              user_id: data.user.id, 
              role: formData.role as "candidate" | "employer_service_provider" | "client",
              approval_status: 'approved'
            }]);

          if (roleError) {
            console.error("Role assignment error:", roleError);
            // Continue - profile created, role can be assigned later
          }
        }

        // GDPR Article 7: Record demonstrable consent with evidence
        const consentRecords: Array<{
          user_id: string;
          purpose: "essential" | "analytics" | "marketing" | "personalization" | "profiling" | "third_party_sharing";
          consent_given: boolean;
          consent_method: string;
          consent_text: string;
          consent_version: string;
          ip_address: any;
          user_agent: string;
        }> = [
          {
            user_id: data.user.id,
            purpose: "essential",
            consent_given: consents.terms,
            consent_method: "signup_checkbox",
            consent_text: "I accept the Terms of Service",
            consent_version: "1.0.0",
            ip_address: null, // Would capture in production: req.ip
            user_agent: navigator.userAgent,
          },
          {
            user_id: data.user.id,
            purpose: "essential",
            consent_given: consents.privacy,
            consent_method: "signup_checkbox",
            consent_text: "I have read and understood the Privacy Policy",
            consent_version: "1.0.0",
            ip_address: null,
            user_agent: navigator.userAgent,
          },
        ];

        // DPA 2018 Schedule 1, Part 2: Criminal conviction data consent (candidates only)
        if (formData.role === "candidate") {
          consentRecords.push({
            user_id: data.user.id,
            purpose: "profiling",
            consent_given: true,
            consent_method: "signup_checkbox",
            consent_text: "I consent to the processing of my criminal record data for employment matching",
            consent_version: "1.0.0",
            ip_address: null,
            user_agent: navigator.userAgent,
          });
        }

        if (consents.data_sharing) {
          consentRecords.push({
            user_id: data.user.id,
            purpose: "third_party_sharing",
            consent_given: true,
            consent_method: "signup_checkbox",
            consent_text: "I consent to sharing my profile with potential employers",
            consent_version: "1.0.0",
            ip_address: null,
            user_agent: navigator.userAgent,
          });
        }

        const { error: consentError } = await supabase
          .from("consent_records")
          .insert(consentRecords);

        if (consentError) {
          console.error("Consent recording error:", consentError);
          // Continue - critical for accountability but not blocking
        }

        // GDPR Article 6: Record lawful basis for processing
        const lawfulBasisRecords: Array<{
          user_id: string;
          processing_purpose: string;
          lawful_basis: "consent" | "contract" | "legal_obligation" | "vital_interests" | "public_task" | "legitimate_interests";
          special_category_condition: "explicit_consent" | "employment_law" | "vital_interests" | "legitimate_activities" | "public_domain" | "legal_claims" | "substantial_public_interest" | null;
        }> = [
          {
            user_id: data.user.id,
            processing_purpose: "Account creation and authentication",
            lawful_basis: "contract",
            special_category_condition: null,
          },
        ];

        if (formData.role === "candidate") {
          lawfulBasisRecords.push({
            user_id: data.user.id,
            processing_purpose: "Criminal record processing for employment",
            lawful_basis: "consent",
            special_category_condition: "substantial_public_interest",
          });
        }

        const { error: lawfulBasisError } = await supabase
          .from("lawful_basis_records")
          .insert(lawfulBasisRecords);

        if (lawfulBasisError) {
          console.error("Lawful basis recording error:", lawfulBasisError);
        }

        toast({
          title: "Account Created",
          description: "Welcome to Second Chance Connect! Let's complete your profile.",
        });

        // Redirect to onboarding
        navigate("/onboarding");
      }
    } catch (error: any) {
      toast({
        title: "Registration Failed",
        description: error.message || "An error occurred during registration.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setValidationErrors({});

    try {
      // Validate input with zod schema
      const validationResult = signInSchema.safeParse({
        email: formData.email,
        password: formData.password,
      });
      
      if (!validationResult.success) {
        const errors: Record<string, string> = {};
        validationResult.error.issues.forEach((err) => {
          if (err.path[0]) {
            errors[err.path[0].toString()] = err.message;
          }
        });
        setValidationErrors(errors);
        setIsLoading(false);
        return;
      }

      const { error, data } = await supabase.auth.signInWithPassword({
        email: formData.email,
        password: formData.password,
      });

      if (error) throw error;

      // Security: Fetch user role to redirect to correct dashboard
      if (data.user) {
        const { data: roleData } = await supabase
          .from("user_roles")
          .select("role, approval_status")
          .eq("user_id", data.user.id)
          .single();

        toast({
          title: "Welcome Back",
          description: "You have successfully signed in.",
        });

        // RBAC: Route to role-specific dashboard
        if (roleData?.role === "candidate") {
          navigate("/candidate/dashboard");
        } else if (roleData?.role === "employer_service_provider") {
          navigate("/employer/dashboard");
        } else if (roleData?.role === "client") {
          navigate("/client/dashboard");
        } else {
          navigate("/");
        }
      }
    } catch (error: any) {
      toast({
        title: "Sign In Failed",
        description: error.message || "Invalid email or password.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setValidationErrors({});

    try {
      // Validate email
      const validationResult = resetPasswordSchema.safeParse({ email: formData.email });
      
      if (!validationResult.success) {
        const errors: Record<string, string> = {};
        validationResult.error.issues.forEach((err) => {
          if (err.path[0]) {
            errors[err.path[0].toString()] = err.message;
          }
        });
        setValidationErrors(errors);
        setIsLoading(false);
        return;
      }

      const { error } = await supabase.auth.resetPasswordForEmail(formData.email, {
        redirectTo: `${window.location.origin}/auth?reset=true`,
      });

      if (error) throw error;

      toast({
        title: "Password Reset Email Sent",
        description: "Check your email for a link to reset your password.",
      });

      setShowResetPassword(false);
    } catch (error: any) {
      toast({
        title: "Reset Failed",
        description: error.message || "Failed to send password reset email.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSocialLogin = async (provider: 'google' | 'apple' | 'linkedin_oidc') => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: `${window.location.origin}/`,
        },
      });

      if (error) throw error;
    } catch (error: any) {
      toast({
        title: "Sign In Failed",
        description: error.message || "An error occurred during social sign in.",
        variant: "destructive",
      });
    }
  };

  if (showResetPassword) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="relative flex flex-col m-6 bg-card shadow-2xl rounded-2xl max-w-md w-full p-8">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowResetPassword(false)}
            className="absolute top-4 left-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>

          <div className="mt-8">
            <h2 className="text-3xl font-bold mb-2">Reset Password</h2>
            <p className="text-muted-foreground mb-6">
              Enter your email address and we'll send you a link to reset your password.
            </p>

            <form onSubmit={handleResetPassword} className="space-y-4">
              <div>
                <Label htmlFor="reset-email" className="mb-2 text-md font-medium">Email Address</Label>
                <Input
                  id="reset-email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full p-3 mt-1"
                  placeholder="you@example.com"
                  required
                />
                {validationErrors.email && (
                  <p className="text-sm text-destructive mt-1">{validationErrors.email}</p>
                )}
              </div>

              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Send Reset Link
              </Button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="relative flex flex-col m-6 bg-card shadow-2xl rounded-2xl md:flex-row md:space-y-0 max-w-5xl w-full">
        {/* Left Side - Value Proposition Carousel */}
        <div className="w-full md:w-1/2">
          <ValuePropositionCarousel />
        </div>

        {/* Right Side - Form */}
        <div className="flex flex-col justify-center p-8 md:p-14 w-full md:w-1/2">
          <h1 className="mb-2 text-3xl font-bold">
            Join the RePath Employer Network
          </h1>
          <p className="font-light text-muted-foreground mb-8">
            Register your organisation to find your next great hire.
          </p>

          <form onSubmit={isSignUp ? handleSignUp : handleSignIn} className="space-y-4">
            {isSignUp && (
              <>
                <div>
                  <Label htmlFor="full_name" className="mb-2 text-md font-medium">Full Name</Label>
                  <Input
                    id="full_name"
                    type="text"
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    className="w-full p-3 mt-1"
                    placeholder="Enter your full name"
                    required
                  />
                  {validationErrors.full_name && (
                    <p className="text-sm text-destructive mt-1">{validationErrors.full_name}</p>
                  )}
                </div>

                <div>
                  <Label htmlFor="job_title" className="mb-2 text-md font-medium">Job Title</Label>
                  <Input
                    id="job_title"
                    type="text"
                    value={formData.job_title}
                    onChange={(e) => setFormData({ ...formData, job_title: e.target.value })}
                    className="w-full p-3 mt-1"
                    placeholder="HR Manager"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="company_name" className="mb-2 text-md font-medium">Company Name</Label>
                  <Input
                    id="company_name"
                    type="text"
                    value={formData.company_name}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                    className="w-full p-3 mt-1"
                    placeholder="Your Company Ltd"
                    required
                  />
                </div>
              </>
            )}

            <div>
              <Label htmlFor="email" className="mb-2 text-md font-medium">Work Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full p-3 mt-1"
                placeholder="you@company.com"
                required
              />
              {validationErrors.email && (
                <p className="text-sm text-destructive mt-1">{validationErrors.email}</p>
              )}
            </div>

            <div>
              <Label htmlFor="password" className="mb-2 text-md font-medium">Create Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full p-3 mt-1 pr-10"
                  placeholder="••••••••"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              {isSignUp && formData.password && (
                <div className="mt-2 space-y-1">
                  <div className="flex gap-1">
                    <div className={`h-1 flex-1 rounded ${formData.password.length >= 8 ? 'bg-secondary' : 'bg-muted'}`}></div>
                    <div className={`h-1 flex-1 rounded ${/[A-Z]/.test(formData.password) && /[a-z]/.test(formData.password) ? 'bg-secondary' : 'bg-muted'}`}></div>
                    <div className={`h-1 flex-1 rounded ${/[0-9]/.test(formData.password) ? 'bg-secondary' : 'bg-muted'}`}></div>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Password strength: {
                      formData.password.length >= 8 && /[A-Z]/.test(formData.password) && /[a-z]/.test(formData.password) && /[0-9]/.test(formData.password)
                        ? 'Strong'
                        : formData.password.length >= 8
                        ? 'Medium'
                        : 'Weak'
                    }
                  </p>
                </div>
              )}
              {validationErrors.password && (
                <p className="text-sm text-destructive mt-1">{validationErrors.password}</p>
              )}
            </div>

            {!isSignUp && (
              <div className="flex justify-between items-center w-full py-2">
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="remember" 
                    checked={rememberMe}
                    onCheckedChange={(checked) => setRememberMe(!!checked)}
                  />
                  <Label htmlFor="remember" className="text-md cursor-pointer">Remember me</Label>
                </div>
                <button
                  type="button"
                  onClick={() => setShowResetPassword(true)}
                  className="font-semibold text-primary hover:underline text-md"
                >
                  Forgot Password?
                </button>
              </div>
            )}

            {isSignUp && (
              <div className="flex items-start space-x-3 mt-4">
                <Checkbox
                  id="terms"
                  checked={consents.terms}
                  onCheckedChange={(checked) => setConsents({ ...consents, terms: checked as boolean, privacy: checked as boolean })}
                  className="mt-1"
                />
                <Label htmlFor="terms" className="text-sm font-normal cursor-pointer">
                  By signing up, you agree to our <a href="/terms" className="text-primary hover:underline font-medium">Terms of Service</a>
                </Label>
              </div>
            )}

            <Button 
              type="submit" 
              className="w-full p-3 mt-6 font-semibold bg-secondary hover:bg-secondary/90 text-secondary-foreground" 
              disabled={isLoading}
            >
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isSignUp ? "Create Company Account" : "Sign In"}
            </Button>
          </form>

          {!isSignUp && (
            <div className="text-center text-muted-foreground mt-4">
              Don't have an account?{" "}
              <button
                type="button"
                onClick={() => setIsSignUp(true)}
                className="font-semibold text-primary hover:underline"
              >
                Sign Up
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
